import { useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';
import { Link } from 'react-router-dom';
import { resolveMediaUrl } from '../utils/url';

export default function AdminMentorMonitoring() {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ mentors: [], trainees: [] });
  const [search, setSearch] = useState('');
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistAssignmentId, setChecklistAssignmentId] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [checklistError, setChecklistError] = useState('');
  const [progressMap, setProgressMap] = useState({}); // assignmentId -> { completed, total, lastCheckedAt }
  const [historyMentorId, setHistoryMentorId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user) return;
      setLoading(true); setError('');
      try {
        const res = await api.get('/admin/mentor-mappings');
        if (!alive) return;
        setData(res.data || { mentors: [], trainees: [] });
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || 'Konnte Daten nicht laden');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  // Nach Laden der Mentor-Daten: alle aktiven Assignment-Checklisten laden und Fortschritt berechnen
  useEffect(() => {
    if (!data.mentors || data.mentors.length === 0) return;
    const ids = [];
    for (const m of data.mentors) {
      if (Array.isArray(m.activeAssignments)) {
        for (const a of m.activeAssignments) ids.push(a.id);
      }
    }
    if (ids.length === 0) return;
    let cancel = false;
    (async () => {
      const next = {};
      await Promise.all(ids.map(async aid => {
        try {
          const res = await api.get(`/admin/mentor-mappings/${aid}/checklist`);
          const items = Array.isArray(res.data?.items) ? res.data.items : [];
          const total = items.length;
          const completed = items.filter(i => i.checked).length;
          let last = null;
          for (const it of items) {
            if (it.checkedAt) {
              const t = new Date(it.checkedAt).getTime();
              if (!last || t > last) last = t;
            }
          }
          next[aid] = { completed, total, lastCheckedAt: last ? new Date(last).toISOString() : null };
        } catch {
          next[aid] = { completed: 0, total: 0, lastCheckedAt: null };
        }
      }));
      if (!cancel) setProgressMap(pm => ({ ...pm, ...next }));
    })();
    return () => { cancel = true; };
  }, [data.mentors]);

  const mentorsFiltered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return data.mentors || [];
    return (data.mentors || []).filter(m =>
      (m.displayName || '').toLowerCase().includes(q) ||
      (m.username || '').toLowerCase().includes(q) ||
      (m.activeAssignments || []).some(a =>
        (a.trainee?.displayName || '').toLowerCase().includes(q) ||
        (a.trainee?.username || '').toLowerCase().includes(q)
      )
    );
  }, [data.mentors, search]);

  const checklistGroups = useMemo(() => {
    const map = {};
    for (const it of checklistItems) {
      if (!map[it.group]) map[it.group] = [];
      map[it.group].push(it);
    }
    return map;
  }, [checklistItems]);

  async function openChecklist(assignmentId) {
    if (!assignmentId) return;
    setChecklistAssignmentId(assignmentId);
    setChecklistOpen(true);
    setChecklistError('');
    setChecklistItems([]);
    setChecklistLoading(true);
    try {
      const res = await api.get(`/admin/mentor-mappings/${assignmentId}/checklist`);
      setChecklistItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      setChecklistError(e?.response?.data?.error || 'Checklist konnte nicht geladen werden');
    } finally {
      setChecklistLoading(false);
    }
  }

  function toggleChecklistItem(key) {
    setChecklistItems(items => items.map(it => it.key === key ? {
      ...it,
      checked: !it.checked,
      checkedAt: !it.checked ? new Date().toISOString() : null
    } : it));
  }
  function updateChecklistNote(key, note) {
    setChecklistItems(items => items.map(it => it.key === key ? { ...it, note } : it));
  }
  async function saveChecklist() {
    if (!checklistAssignmentId) return;
    setChecklistSaving(true);
    setChecklistError('');
    try {
      await api.post(`/admin/mentor-mappings/${checklistAssignmentId}/checklist`, {
        items: checklistItems.map(it => ({ key: it.key, checked: it.checked, note: it.note }))
      });
      const res = await api.get(`/admin/mentor-mappings/${checklistAssignmentId}/checklist`);
      setChecklistItems(Array.isArray(res.data?.items) ? res.data.items : []);
      // Fortschritts-Metriken für dieses Assignment aktualisieren
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      const total = items.length;
      const completed = items.filter(i => i.checked).length;
      let last = null;
      for (const it of items) { if (it.checkedAt) { const t = new Date(it.checkedAt).getTime(); if (!last || t > last) last = t; } }
      setProgressMap(pm => ({ ...pm, [checklistAssignmentId]: { completed, total, lastCheckedAt: last ? new Date(last).toISOString() : null } }));
    } catch (e) {
      setChecklistError(e?.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally { setChecklistSaving(false); }
  }

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width:'100vw', margin:0, padding:0 }}>
        <h1 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Mentoring-Überwachung (Admin)</h1>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Überblick über Mentoren, laufende Mentorships und Fortschritt.</p>
        <p style={{ textAlign: 'center', maxWidth: 820, margin: '0 auto', color: '#D4D8DF' }}>
          Nutze diese Seite, um Betreuung, Aktivität und Fortschritt konsistent zu halten – und bei Bedarf gezielt zu unterstützen.
        </p>
      </section>

      <section style={{ width:'100%', maxWidth:1200, margin:'0 auto', padding: '0 16px 60px', color:'#fff' }}>
        {error && (
          <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(248,113,113,0.6)', color: '#fecaca' }}>{error}</div>
        )}
        {loading ? <p>Lade Daten…</p> : (
          <>
            <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:16, alignItems:'center' }}>
              <div style={{ fontSize:13, opacity:.85 }}>
                <strong>Mentoren:</strong> {data.mentors?.length || 0} · <strong>Aktive Anwärter:</strong> {(data.mentors||[]).reduce((s,m)=> s + (m.activeCount||0), 0)}
              </div>
              <div style={{ flexGrow:1 }} />
              <input type="text" placeholder="Mentor oder Anwärter suchen" value={search} onChange={e=> setSearch(e.target.value)}
                style={{ minWidth: 220, maxWidth: 280, flexShrink: 0, padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,0.6)', background: '#020617', color: '#e5e7eb', fontSize: 13 }} />
            </div>

            <div className="highlight-card" style={{ padding:20 }}>
              <h2 className="highlight-title">Mentoren-Übersicht</h2>
              <p style={{ color:'#cbd5e1', fontSize:14, lineHeight:1.5 }}>Aktive Zuweisungen je Mentor, inkl. schneller Zugriff auf Checklisten.</p>
              <div style={{ marginTop:10 }}>
                {(!mentorsFiltered || mentorsFiltered.length === 0) ? (
                  <p style={{ color:'#9ca3af', fontSize:14 }}>Keine Mentoren gefunden.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
                    {mentorsFiltered.map(m => {
                      const active = Array.isArray(m.activeAssignments) ? m.activeAssignments : [];
                      return (
                        <div key={m.id} style={{ background:'#0b1624', border:'1px solid #1f2a44', borderRadius:18, padding:'16px 18px', boxShadow:'0 6px 18px -4px rgba(0,0,0,0.45)', position:'relative' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                            <Link to={`/u/${m.id}`} style={{ display:'inline-block' }}>
                              <img src={resolveMediaUrl(m.avatarUrl || '/avatars/default-avatar.png')} alt="Mentor Avatar" style={{ width:56, height:56, borderRadius:'50%', objectFit:'cover', border:'2px solid #1e3347' }} />
                            </Link>
                            <div style={{ flexGrow:1, minWidth:220 }}>
                              <div style={{ fontSize:18, fontWeight:600, letterSpacing:.3 }}>
                                <Link to={`/u/${m.id}`} style={{ color:'#fff', textDecoration:'none' }}>{m.displayName || m.username}</Link>
                              </div>
                              <div style={{ fontSize:12, opacity:.7 }}>Mentor seit {m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '-'}</div>
                            </div>
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                              <button type="button" onClick={() => setHistoryMentorId(m.id)} style={{ background:'rgba(147,197,253,0.08)', border:'1px solid #334155', color:'#93c5fd', padding:'6px 12px', fontSize:12, borderRadius:8, cursor:'pointer' }}>
                                Historie: {Array.isArray(m.historyAssignments) ? m.historyAssignments.length : 0}
                              </button>
                              <div style={{ fontSize:11, color:'#94a3b8' }}>Aktive Anwärter: {active.length}</div>
                            </div>
                          </div>
                          <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:14 }}>
                            {active.length === 0 ? (
                              <div style={{ fontSize:13, color:'#64748b' }}>Keine aktiven Anwärter.</div>
                            ) : active.map(a => (
                              <div key={a.id} style={{ background:'#0d1e30', border:'1px solid #20354b', borderRadius:14, padding:'12px 14px', display:'flex', flexDirection:'column', gap:8 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                                  <img src={resolveMediaUrl(a.trainee?.avatarUrl || '/avatars/default-avatar.png')} alt="Avatar" style={{ width:30, height:30, borderRadius:'50%', objectFit:'cover', border:'2px solid #1e3347' }} />
                                  <div style={{ display:'flex', flexDirection:'column', minWidth:160 }}>
                                    <Link to={`/u/${a.traineeId}`} style={{ color:'#fff', textDecoration:'none', fontWeight:600 }}>{a.trainee?.displayName || a.trainee?.username || `User #${a.traineeId}`}</Link>
                                    <div style={{ fontSize:11, opacity:.65 }}>seit {a.assignedAt ? new Date(a.assignedAt).toLocaleDateString() : '-'}</div>
                                  </div>
                                  <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
                                    <button type="button" onClick={() => openChecklist(a.id)} style={{ fontSize:12, padding:'6px 14px', borderRadius:999, border:'1px solid rgba(16,185,129,0.6)', background:'rgba(6,78,59,0.85)', color:'#6ee7b7', cursor:'pointer' }}>Checkliste öffnen</button>
                                  </div>
                                </div>
                                {renderProgressBar(progressMap[a.id])}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {checklistOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2050, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }} onClick={()=> !checklistSaving && setChecklistOpen(false)}>
          <div role="dialog" aria-modal="true" aria-label="Mentor-Checkliste" onClick={e=> e.stopPropagation()} style={{ width:'100%', maxWidth:1000, maxHeight:'86vh', background:'#0d1b29', border:'1px solid #1e3347', borderRadius:18, boxShadow:'0 18px 50px -6px rgba(0,0,0,0.55)', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'18px 24px', borderBottom:'1px solid #1e3347', display:'flex', alignItems:'center', justifyContent:'space-between', background:'linear-gradient(180deg, rgba(14,120,175,0.35), rgba(2,6,23,0))', color:'#e2e8f0' }}>
              <h2 style={{ margin:0, fontSize:20 }}>Mentor-Checkliste</h2>
              <div style={{ display:'flex', gap:10 }}>
                <button type="button" disabled={checklistSaving} onClick={saveChecklist} style={{ background:'#065f46', color:'#d1fae5', border:'1px solid #10b981', padding:'8px 16px', fontSize:13, borderRadius:10, cursor: checklistSaving ? 'default':'pointer', opacity: checklistSaving?0.7:1 }}>Speichern{checklistSaving?'…':''}</button>
                <button type="button" onClick={()=> !checklistSaving && setChecklistOpen(false)} style={{ background:'transparent', color:'#93c5fd', border:'1px solid #334155', padding:'8px 14px', fontSize:13, borderRadius:10, cursor:'pointer' }}>Schließen</button>
              </div>
            </div>
            <div style={{ padding:'16px 20px', fontSize:13, color:'#cbd5e1' }}>
              {checklistError && <div style={{ background:'rgba(220,38,38,0.18)', border:'1px solid rgba(248,113,113,0.5)', color:'#fecaca', padding:'8px 10px', borderRadius:8, marginBottom:12 }}>{checklistError}</div>}
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'0 20px 20px', display:'flex', flexDirection:'column', gap:18 }}>
              {checklistLoading ? (
                <div style={{ color:'#94a3b8', fontSize:14 }}>Lade Checkliste…</div>
              ) : (
                Object.keys(checklistGroups).map(group => (
                  <div key={group} style={{ background:'#112638', border:'1px solid #1e3347', borderRadius:14, padding:16 }}>
                    <div style={{ fontWeight:600, fontSize:15, marginBottom:10, color:'#93c5fd' }}>{group}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {checklistGroups[group].map(it => (
                        <div key={it.key} style={{ display:'flex', flexDirection:'column', gap:6, background: it.checked ? 'linear-gradient(90deg, rgba(6,95,70,0.55), rgba(6,95,70,0.15))':'#162b3b', border:'1px solid '+(it.checked ? '#10b981':'#1e3347'), padding:'10px 12px', borderRadius:10 }}>
                          <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                            <input type="checkbox" checked={!!it.checked} onChange={() => toggleChecklistItem(it.key)} style={{ width:16, height:16 }} />
                            <span style={{ fontSize:14, color:'#e2e8f0' }}>{renderChecklistLabel(it.key)}</span>
                            {it.checkedAt && (
                              <span style={{ marginLeft:'auto', fontSize:11, color:'#d1fae5', background:'rgba(16,185,129,0.18)', padding:'2px 6px', borderRadius:6 }}>
                                {formatChecklistDate(it.checkedAt)}
                              </span>
                            )}
                          </label>
                          <textarea
                            value={it.note || ''}
                            onChange={e => updateChecklistNote(it.key, e.target.value.slice(0,300))}
                            placeholder="Notiz (optional)"
                            style={{ width:'100%', resize:'vertical', minHeight:50, background:'#0d1b29', border:'1px solid #1e3347', color:'#e2e8f0', fontSize:12, padding:'6px 8px', borderRadius:8 }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {historyMentorId && renderHistoryPopup(historyMentorId, data.mentors, setHistoryMentorId)}
    </div>
  );
}

function renderChecklistLabel(key) {
  const map = {
    mentee_begruesst: 'Mentee aktiv begrüßt',
    mentor_vorgestellt: 'Mentor kurz vorgestellt',
    ziel_erklaert: 'Mentoring-Ziel erklärt',
    dauer_erklaert: 'Dauer des Mentorings erklärt',
    grenzen_erklaert: 'Grenzen erklärt',
    kommunikationsweg_abgesprochen: 'Kommunikationsweg(e) abgesprochen',
    erreichbarkeit_geteilt: 'Erreichbarkeitszeiten geteilt',
    mentee_erwartungen_gefragt: 'Erwartungen/Ziele abgefragt',
    start_ziele_definiert: 'Gemeinsame Start-Ziele definiert',
    erster_check_in_termin: 'Erster Check-In Termin vereinbart',
    grundprinzip_erklaert: 'Grundprinzip AP-Network erklärt',
    rollen_erklaert: 'Rollen erklärt',
    discord_areas_erklaert: 'Wichtige Discord-Areas gezeigt',
    plattform_bereiche_erklaert: 'Wichtige Plattform-Bereiche gezeigt',
    regeln_erklaert: 'Regeln/Grundwerte erklärt',
    typische_fragen_beantwortet: 'Typische Anfängerfragen beantwortet',
    erste_interaktionen_motiviert: 'Zu ersten Interaktionen motiviert',
    dashboard_geoeffnet: 'Plattform-Dashboard gemeinsam geöffnet',
    streamplan_gezeigt: 'Streamplan gezeigt',
    streamplan_logik_erklaert: 'Streamplan-Logik erklärt',
    punkte_logik_erklaert: 'Punkte-Grundlogik erklärt',
    punkte_aktionen_erklaert: 'Konkrete Punkte-Aktionen erklärt',
    achievements_erklaert: 'Achievements erklärt & gezeigt',
    punkte_rueckfragen_beantwortet: 'Rückfragen zu Punkten beantwortet',
    basics_selbststaendig_test: 'Basics selbstständig bedient (Test)',
    setup_stand_erfragt: 'Setup-Stand erfragt',
    hauptbaustellen_identifiziert: 'Hauptbaustellen identifiziert',
    quick_wins_vorgeschlagen: 'Technische Quick-Wins vorgeschlagen',
    setup_test_durchgesprochen: 'Setup-Test durchgesprochen',
    fehlerquellen_genannt: 'Typische Fehlerquellen genannt',
    lurker_zweck_erklaert: 'Lurker-Client Zweck erklärt',
    lurker_installation_begleitet: 'Installation begleitet',
    lurker_login_flow: 'Login-/Profil-Flow erklärt',
    lurker_einstellungen_erklaert: 'Relevante Einstellungen erklärt',
    lurker_fair_use: 'Fair-Use Hinweise erklärt',
    lurker_troubleshooting: 'Troubleshooting begleitet',
    events_gezeigt: 'Events gezeigt/erklärt',
    challenges_gezeigt: 'Challenges gezeigt/erklärt',
    teilnahme_regeln_erklaert: 'Teilnahme-Regeln erklärt',
    erste_anmeldung_begleitet: 'Erste Anmeldung begleitet',
    teilnahme_motiviert: 'Teilnahme motiviert',
    feedback_bedarf_gefragt: 'Feedback-Bedarf erfragt',
    stream_angeschaut: 'Stream/Content angeschaut',
    feedback_stream_gegeben: 'Feedback zum Stream gegeben',
    feedback_auftreten_gegeben: 'Feedback Auftreten/Moderation',
    feedback_community_gegeben: 'Feedback Community-Verhalten',
    positive_stärken: 'Positive Stärken betont',
    naechste_schritte_definiert: 'Nächste Schritte definiert',
    feedback_anwenden_motiviert: 'Feedback-Anwendung motiviert',
    interaktion_pro_woche: 'Mind. 1 Interaktion pro Woche',
    regelmaessige_check_ins: 'Regelmäßige Check-Ins durchgeführt',
    fortschritt_geprueft: 'Fortschritt zu Start-Zielen geprüft',
    selbststaendigkeit_vorbereitet: 'Auf Selbstständigkeit vorbereitet',
    abschluss_check_in: 'Abschluss-Check-In gemacht',
    verlaengerung_besprochen: 'Verlängerung besprochen',
    mentoring_beendet_motiviert: 'Mentoring beendet + motiviert'
  };
  return map[key] || key;
}
function formatChecklistDate(ts) {
  try { return new Date(ts).toLocaleDateString('de-DE', { year:'numeric', month:'2-digit', day:'2-digit' }); } catch { return ts; }
}

function renderProgressBar(progress) {
  if (!progress) return <div style={{ fontSize:11, color:'#94a3b8', marginTop:6 }}>Lade Fortschritt…</div>;
  const { completed, total, lastCheckedAt } = progress;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const stale = lastCheckedAt ? (Date.now() - new Date(lastCheckedAt).getTime() > 7 * 24 * 3600 * 1000) : false;
  return (
    <div style={{ marginTop:4, display:'flex', flexDirection:'column', gap:6 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ flexGrow:1, height:8, background:'#1e3347', borderRadius:6, overflow:'hidden', position:'relative' }}>
          <div style={{ height:'100%', width:`${percent}%`, background:'linear-gradient(90deg,#059669,#10b981)', transition:'width .35s ease' }} />
        </div>
        <div style={{ minWidth:42, textAlign:'right', fontSize:12, fontWeight:600, color:'#e2e8f0' }}>{percent}%</div>
      </div>
      <div style={{ fontSize:11, display:'flex', justifyContent:'space-between', color:'#94a3b8' }}>
        <span>{completed}/{total} erledigt</span>
        <span style={{ color: lastCheckedAt ? (stale ? '#f87171' : '#6ee7b7') : '#94a3b8' }}>{lastCheckedAt ? formatChecklistDate(lastCheckedAt) : 'kein Eintrag'}</span>
      </div>
    </div>
  );
}

function renderHistoryPopup(mentorId, mentors, closeFn) {
  const mentor = (mentors || []).find(m => m.id === mentorId);
  const items = mentor?.historyAssignments || [];
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2100, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }} onClick={() => closeFn(null)}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:560, maxHeight:'80vh', overflowY:'auto', background:'#0f1e2d', border:'1px solid #1e3347', borderRadius:16, padding:20, boxShadow:'0 16px 40px -6px rgba(0,0,0,0.5)', color:'#e2e8f0', fontSize:13 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <h3 style={{ margin:0, fontSize:18 }}>Historische Zuordnungen</h3>
          <button type="button" onClick={() => closeFn(null)} style={{ background:'transparent', border:'1px solid #334155', color:'#93c5fd', padding:'6px 12px', borderRadius:8, cursor:'pointer', fontSize:12 }}>Schließen</button>
        </div>
        {items.length === 0 ? (
          <p style={{ color:'#94a3b8' }}>Keine historischen Zuordnungen.</p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {items.map(a => (
              <div key={a.id} style={{ background:'#142536', border:'1px solid #1e3347', borderRadius:10, padding:'10px 12px', display:'flex', flexDirection:'column', gap:4 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <Link to={`/u/${a.traineeId}`} style={{ color:'#fff', fontWeight:600, textDecoration:'none' }}>{a.trainee?.displayName || a.trainee?.username || `User #${a.traineeId}`}</Link>
                  <span style={{ fontSize:11, opacity:.7 }}>#{a.traineeId}</span>
                  <span style={{ marginLeft:'auto', fontSize:11, color:'#93c5fd' }}>{a.assignedAt ? formatChecklistDate(a.assignedAt) : '?'} → {a.endedAt ? formatChecklistDate(a.endedAt) : '?'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
